import React from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CostCenterDetailFlag } from '../../../types';

import { useGetEmployerOptions } from '../../../../../Employer/EmployerCostSettings/components/Sections/General/hooks/useGetEmployerOptions';

import {
  ControlledAutoComplete,
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect
} from 'components/shared';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import useCreateCostCenterDetailOptions from '../../../hooks/useCreateCostCenterDetailOptions';
import { useGetCostCenterDetails } from '../../../hooks/useGetCostCenterDetails';
import { orderBy } from 'lodash';

interface Props {
  name?: string;
}

type ParentCostCenterFieldProps = Props & {
  selectedBrDynId?: number;
};

export const CodeField: React.FC<Props> = ({ name = 'code' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.code')}
    />
  );
};

export const NameField: React.FC<Props> = ({ name = 'name' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.name')}
    />
  );
};

export const NameShortField: React.FC<Props> = ({ name = 'nameShort' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.shortName')}
    />
  );
};

export const CodeExtField: React.FC<Props> = ({ name = 'codeExt' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.exportCode')}
    />
  );
};

export const CodeExtSecondaryField: React.FC<Props> = ({
  name = 'codeExtSecondary'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.exportCode2')}
    />
  );
};

export const CodeImpField: React.FC<Props> = ({ name = 'codeImp' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importCode')}
    />
  );
};

export const CodeImpSecondaryField: React.FC<Props> = ({
  name = 'codeImpSecondary'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importCode2')}
    />
  );
};

export const BenefitStartDateField: React.FC<Props> = ({
  name = 'benefitStartDate'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importStartDate')}
      fullWidth={true}
    />
  );
};

export const FinancialCostCenterField: React.FC<Props> = ({
  name = 'finBrDynId'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const { data: costCenterDetails } = useGetCostCenterDetails();
  const costCenterDetailOptions =
    useCreateCostCenterDetailOptions(costCenterDetails);

  return (
    <ControlledAutoComplete
      control={control}
      variant="outlined"
      label={t('costCenterDetail.financialCostCenter')}
      name={name}
      options={orderBy(
        costCenterDetailOptions,
        ['validFrom', 'label'],
        ['desc', 'asc']
      )}
      idProperty="value"
      optionLabelProp="label"
      disableSorting
      fullWidth
    />
  );
};

export const ParentCostCenterField: React.FC<ParentCostCenterFieldProps> = ({
  name = 'parentBrDynId',
  selectedBrDynId
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const { data: costCenterDetails } = useGetCostCenterDetails();
  const costCenterDetailOptions =
    useCreateCostCenterDetailOptions(costCenterDetails);

  // remove the current selectedBrDynId from the options
  const filteredCostCenterDetailOptions = costCenterDetailOptions.filter(
    (option) => option.value !== selectedBrDynId
  );

  return (
    <ControlledAutoComplete
      control={control}
      variant="outlined"
      label={t('costCenterDetail.parentCostCenter')}
      name={name}
      options={orderBy(
        selectedBrDynId
          ? filteredCostCenterDetailOptions
          : costCenterDetailOptions,
        ['validFrom', 'label'],
        ['desc', 'asc']
      )}
      idProperty="value"
      optionLabelProp="label"
      disableSorting
      fullWidth
    />
  );
};

export const BUHBlockedField: React.FC<Props> = ({
  name = `flags[${CostCenterDetailFlag.BUH_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.noBUH')}
    />
  );
};

export const PlanningExportField: React.FC<Props> = ({
  name = `flags[${CostCenterDetailFlag.PLANNING_EXPORT}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.includeInExport')}
    />
  );
};

export const VATDeductibleField: React.FC<Props> = ({
  name = `flags[${CostCenterDetailFlag.VAT_DEDUCTIBLE}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.VATDeductible')}
    />
  );
};

export const InvoiceDebitBlockedField: React.FC<Props> = ({
  name = `flags[${CostCenterDetailFlag.INVOICE_DEBIT_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.excludeBorrowFromInvoice')}
    />
  );
};

export const InvoiceCreditBlockedField: React.FC<Props> = ({
  name = `flags[${CostCenterDetailFlag.INVOICE_CREDIT_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.excludeLendFromInvoice')}
    />
  );
};

export const ValidFromField: React.FC<Props> = ({ name = 'valid.from' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('validFrom')}
      fullWidth={true}
    />
  );
};

export const ValidToField: React.FC<Props> = ({ name = 'valid.to' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('validTo')}
      fullWidth={true}
    />
  );
};

export const EmployerField: React.FC<Props> = ({ name = 'employerId' }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const employerOptions = useGetEmployerOptions();

  return (
    <ControlledSelect
      control={control}
      variant="outlined"
      label={t('costCenterDetail.employer')}
      name={name}
      options={employerOptions}
    />
  );
};
